import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import AddressAutocomplete from '../shared/AddressAutocomplete';

const ClientFormModal = ({ client, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: client?.firstName || '',
    lastName: client?.lastName || '',
    email: client?.email || '',
    loginIdentifier: client?.loginIdentifier || '',
    password: '',
    phone: client?.phone || '',
    address: client?.address || '',
    dateOfBirth: client?.dateOfBirth || '',
    profession: client?.profession || '',
    monthlyIncome: client?.monthlyIncome || '',
    loanAmount: client?.loanAmount || '',
    contribution: client?.contribution || '',
    loanDuration: client?.loanDuration || '',
    status: client?.status || 'En attente'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      monthlyIncome: Number(formData.monthlyIncome),
      loanAmount: Number(formData.loanAmount),
      contribution: Number(formData.contribution),
      loanDuration: Number(formData.loanDuration)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            {client ? 'Modifier le Client' : 'Nouveau Client'}
          </h2>
          <Button onClick={onClose} variant="ghost" size="sm" className="p-2">
            <X size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informations Personnelles */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Informations Personnelles</h3>
            </div>

            <div>
              <Label htmlFor="firstName">Prénom *</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="lastName">Nom *</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="loginIdentifier">Identifiant</Label>
              <Input
                id="loginIdentifier"
                type="text"
                value={formData.loginIdentifier}
                onChange={(e) => handleChange('loginIdentifier', e.target.value)}
                placeholder="Chiffres ou email (laisser vide → email)"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Identifiant choisi pour la connexion (chiffres ou email).
                Si vide, l&apos;email sera utilisé. Choisissez un identifiant unique.
              </p>
            </div>

            <div>
              <Label htmlFor="password">Mot de passe *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required={!client}
                placeholder="Mot de passe pour se connecter"
                className="mt-2"
              />
              {!client && (
                <p className="text-xs text-gray-500 mt-1">
                  Ce mot de passe servira au client pour se connecter
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
                className="mt-2"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="address">Adresse *</Label>
              <AddressAutocomplete
                id="address"
                value={formData.address}
                onChange={(val) => handleChange('address', val)}
                required
                className="mt-2"
                placeholder="Commencez à taper votre adresse..."
              />
            </div>

            <div>
              <Label htmlFor="dateOfBirth">Date de naissance *</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="profession">Profession *</Label>
              <Input
                id="profession"
                value={formData.profession}
                onChange={(e) => handleChange('profession', e.target.value)}
                required
                className="mt-2"
              />
            </div>

            {/* Informations Financières */}
            <div className="md:col-span-2 mt-4">
              <h3 className="text-lg font-semibold mb-4">Informations Financières</h3>
            </div>

            <div>
              <Label htmlFor="monthlyIncome">Revenus mensuels (€) *</Label>
              <Input
                id="monthlyIncome"
                type="number"
                value={formData.monthlyIncome}
                onChange={(e) => handleChange('monthlyIncome', e.target.value)}
                required
                className="mt-2"
                min="0"
              />
            </div>

            <div>
              <Label htmlFor="loanAmount">Montant du prêt (€) *</Label>
              <Input
                id="loanAmount"
                type="number"
                value={formData.loanAmount}
                onChange={(e) => handleChange('loanAmount', e.target.value)}
                required
                className="mt-2"
                min="0"
              />
            </div>

            <div>
              <Label htmlFor="contribution">Apport personnel (€) *</Label>
              <Input
                id="contribution"
                type="number"
                value={formData.contribution}
                onChange={(e) => handleChange('contribution', e.target.value)}
                required
                className="mt-2"
                min="0"
              />
            </div>

            <div>
              <Label htmlFor="loanDuration">Durée du prêt (années) *</Label>
              <Input
                id="loanDuration"
                type="number"
                value={formData.loanDuration}
                onChange={(e) => handleChange('loanDuration', e.target.value)}
                required
                className="mt-2"
                min="1"
                max="30"
              />
            </div>

            <div>
              <Label htmlFor="status">Statut *</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="En attente">En attente</SelectItem>
                  <SelectItem value="En cours">En cours</SelectItem>
                  <SelectItem value="Approuvé">Approuvé</SelectItem>
                  <SelectItem value="Refusé">Refusé</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" style={{ backgroundColor: '#2563EB', color: 'white' }}>
              {client ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientFormModal;